import { action, observable, autorun, computed } from 'mobx';
// modules
import { LStorage } from 'modules/Storage';
// consts
import { DEFAULT_OPTION_CHAIN_TABLE_SETTINGS } from './consts';

export default class OptionChainStore {
  // observables
  @observable exchangeToken = '';

  @observable symbol = '';

  @observable spotToken = '';

  @observable scripData: DataSchema | null = null;

  @observable spotScripData: Partial<DataSchema> | null = null;

  @observable expiries: number[] = [];

  @observable selectedExpiry: number | null = null;

  @observable strikes: StrikeSchema[] = [];

  @observable futureScrip: DataSchema | null = null;

  @observable strikesAmountToShow = 20;

  @observable activeStrikes: StrikeSchema[] = [];

  @observable activeStrikesWithSpot: StrikeSchema[] = [];

  @observable spotIndex = 10;

  @observable callTableData: OptionChainRowData[] = [];

  @observable putTableData: OptionChainRowData[] = [];

  @observable allStrikesSpotIndex = -1;

  @observable activeTokens: string[] = [];

  @observable maxOI = 0;

  @observable maxTotalVolume = 0;

  @observable openSettings = false;

  @observable isOptionChainAvailable = true;

  @observable isMinimised = false;

  @observable dragging = false;

  @observable tableSettings: OCTableSettings =
    LStorage.getItem('OC_TABLE_SETTINGS') ||
    DEFAULT_OPTION_CHAIN_TABLE_SETTINGS;

  tableSettingsAutorun = autorun(() => {
    LStorage.sync('OC_TABLE_SETTINGS', this.tableSettings);
  });

  // actions
  @action setCurrentScrip = (value: string) => {
    this.exchangeToken = value;
  };

  @action setSpotToken = (value: string) => {
    this.spotToken = value;
  };

  @action setAllStrikesSpotIndex = (value: number) => {
    this.allStrikesSpotIndex = value;
  };

  @action setMaxOI = (value: number) => {
    this.maxOI = value;
  };

  @action setMaxTotalVolume = (value: number) => {
    this.maxTotalVolume = value;
  };

  @action setScripData = (value: DataSchema | null) => {
    this.scripData = value;
  };

  @action setSpotScripData = (value: Partial<DataSchema> | null) => {
    this.spotScripData = value;
  };

  @action setExpiries = (value: number[]) => {
    this.expiries = value;
  };

  @action setStrikesAmountToShow = (value: number) => {
    this.strikesAmountToShow = value;
  };

  @action setSelectedExpiry = (value: number | null) => {
    this.selectedExpiry = value;
  };

  @action setIsOptionChainAvailable = (value: boolean) => {
    this.isOptionChainAvailable = value;
  };

  @action setSymbol = (value: string) => {
    this.symbol = value;
  };

  @action clear = () => {
    this.exchangeToken = '';
    this.symbol = '';
    this.spotToken = '';
    this.scripData = null;
    this.expiries = [];
    this.strikes = [];
    this.activeStrikes = [];
    this.activeStrikesWithSpot = [];
    this.spotIndex = 10;
    this.activeTokens = [];
    this.strikesAmountToShow = 20;
    this.allStrikesSpotIndex = -1;
    this.maxOI = 0;
    this.maxTotalVolume = 0;
    this.futureScrip = null;
    this.spotScripData = null;
    this.isOptionChainAvailable = true;
    this.callTableData = [];
    this.putTableData = [];
  };

  @action clearOnExpiryChange = () => {
    this.strikes = [];
    this.activeStrikes = [];
    this.callTableData = [];
    this.putTableData = [];
    this.activeStrikesWithSpot = [];
    this.spotIndex = 10;
    this.allStrikesSpotIndex = -1;
  };

  @action setStrikes = (value: StrikeSchema[]) => {
    this.strikes = value;
  };

  @action setFutureScrip = (value: DataSchema) => {
    this.futureScrip = value;
  };

  @action setActiveStrikes = (value: StrikeSchema[]) => {
    this.activeStrikes = value;
  };

  @action setActiveStrikesWithSpot = (value: StrikeSchema[]) => {
    this.activeStrikesWithSpot = value;
  };

  @action setSpotIndex = (value: number) => {
    this.spotIndex = value;
  };

  @action setRevampedOptionChainData = (
    callTableData: OptionChainRowData[],
    putTableData: OptionChainRowData[],
  ) => {
    this.callTableData = callTableData;
    this.putTableData = putTableData;
  }

  @action setActiveTokens = (value: string[]) => {
    this.activeTokens = value;
  };

  @action setTableSettings = (value: Partial<OCTableSettings>) => {
    this.tableSettings = {
      ...this.tableSettings,
      ...value,
    };
  };

  @action setOpenSettings = (value: boolean) => {
    this.openSettings = value;
  }

  @action setIsMinimised = (value: boolean) => {
    this.isMinimised = value;
  }

  @action setDragging = (value: boolean) => {
    this.dragging = value;
  }

  @computed get loading() {
    return (
      !this.strikes.length ||
      !this.callTableData.length ||
      !this.putTableData.length ||
      !this.selectedExpiry ||
      !this.activeStrikes.length
    );
  }
}
